import { createApi } from "@reduxjs/toolkit/query/react";
import { apiFunction } from "../utils";
import { baseQueryWithReauth } from "../baseQueryWithReauth";
import { serializeArgsWithoutToken } from "../serializeQueryArgs";

const devicesApi = createApi({
  reducerPath: "devices",
  baseQuery: baseQueryWithReauth,
  serializeQueryArgs: serializeArgsWithoutToken, // #142: token önbellek anahtarına girmez
  tagTypes: ["ManualQuotaRequest", "PaymentRejection"],
  keepUnusedDataFor: 120,
  endpoints(builder) {
    return {
      uploadManualQuota: builder.mutation({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/uploadManualQuota", "PATCH");
        },
      }),
      uploadManualQR: builder.mutation({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/uploadManualQR", "PATCH");
        },
      }),
      // Manuel kota talebi (QR, makine 3.9.4+) — cihaz bilgi ekranı kartı (#115).
      // Sözleşme: BayinerIOT-Workspace- docs/api-contracts/manual-quota-qr.md §4
      getManualQuotaRequest: builder.query({
        query: (data) =>
          apiFunction(data, "/api/devices/storageRoles/manualQuotaRequest", "POST"),
        providesTags: (result, error, arg) => [{ type: "ManualQuotaRequest", id: arg?.deviceId }],
      }),
      // Reddedilen ödemeler (#410) — QR/Espresso/NFC retleri, son 90 gün.
      // Sözleşme: BayinerIOT-Workspace- docs/api-contracts/employee-only-payment-device.md §10.1
      getPaymentRejections: builder.query({
        query: (data) =>
          apiFunction(data, "/api/devices/storageRoles/getPaymentRejections", "POST"),
        providesTags: (result, error, arg) => [{ type: "PaymentRejection", id: arg?.id }],
        keepUnusedDataFor: 30,
      }),
      generateManualQuotaRequestCode: builder.mutation({
        query: ({ deviceId, ...data }) =>
          apiFunction(data, "/api/devices/storageRoles/manualQuotaRequest/generateCode", "PATCH"),
        invalidatesTags: (result, error, arg) => [{ type: "ManualQuotaRequest", id: arg?.deviceId }],
      }),
      updateSetting: builder.mutation({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/uploadDeviceSetting", "PATCH");
        },
      }),
      dowloadSetting: builder.mutation({
        query: (data) => {
          return apiFunction(
            data,
            "/api/devices/storageRoles/downloadDeviceSetting",
            "POST"
          );
        },
      }),
      updateDeviceInformation: builder.mutation({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/updateDevice", "PATCH");
        },
      }),
      updateDeviceSerialNo: builder.mutation({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/updateDeviceSerialNo", "PATCH");
        },
      }),
      updateDeviceName: builder.mutation({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/updateDeviceName", "PATCH");
        },
      }),
      deleteDevice: builder.mutation({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/deleteDevice", "DELETE");
        },
      }),
      updateDeviceSync: builder.mutation({
        query: (data) => {
          return apiFunction(
            data,
            "/api/devices/storageRoles/updateDeviceSyncSetting",
            "PATCH"
          );
        },
      }),
      removeDeviceProduct: builder.mutation({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/removeDeviceProduct", "PATCH");
        },
      }),
      getDeviceDetail: builder.query({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/getDeviceDetail", "POST");
        },
      }),
      getDeviceDistribution: builder.query({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/getDeviceDistribution", "POST");
        },
      }),
      getDeviceById: builder.query({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/getDeviceDetail", "POST");
        },
      }),
      getDeviceLastOperation: builder.mutation({
        query: (data) => {
          return apiFunction(
            data,
            "/api/devices/storageRoles/getDeviceLastOperation",
            "POST"
          );
        },
      }),
      getDeviceMovementHistory: builder.query({
        query: (data) =>
          apiFunction(
            data,
            "/api/devices/storageRoles/getDeviceMovementHistory",
            "POST"
          ),
      }),
      getDevices: builder.query({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/getDevices", "POST");
        },
      }),
      // Rapor sekmesi → Tüketim (#343): PDF + Excel sayfaları
      getDeviceDetailPerformanceReport: builder.mutation({
        query: (data) =>
          apiFunction(data, "/api/devices/storageRoles/performance-report", "POST"),
      }),
      getDeviceConsumentLogsforReport: builder.mutation({
        query: (data) => {
          return apiFunction(
            data,
            "/api/devices/storageRoles/getDeviceConsumentLogsforReport",
            "POST"
          );
        },
      }),
      // Tekil tüketim kaydının firma/durum düzeltmesi — sadece admin
      updateDeviceConsumentLog: builder.mutation({
        query: (data) =>
          apiFunction(
            data,
            "/api/devices/storageRoles/updateDeviceConsumentLog",
            "PATCH"
          ),
      }),
      getDeviceSettingLogsforReport: builder.mutation({
        query: (data) => {
          return apiFunction(
            data,
            "/api/devices/storageRoles/getDeviceSettingLogsforReport",
            "POST"
          );
        },
      }),
      getDeviceManualQuotaLogsforReport: builder.mutation({
        query: (data) => {
          return apiFunction(
            data,
            "/api/devices/storageRoles/getDeviceManualQuotaLogsforReport",
            "POST"
          );
        },
      }),
      getDeviceQuotaChangeLogsforReport: builder.mutation({
        query: (data) => {
          return apiFunction(
            data,
            "/api/devices/storageRoles/getDeviceQuotaChangeLogsforReport",
            "POST"
          );
        },
      }),
      getDeviceInfoLogsforReport: builder.mutation({
        query: (data) => {
          return apiFunction(
            data,
            "/api/devices/storageRoles/getDeviceInfoLogsforReport",
            "POST"
          );
        },
      }),
      getDeviceOperationLogsforReport: builder.mutation({
        query: (data) => {
          return apiFunction(
            data,
            "/api/devices/storageRoles/getDeviceOperationLogsforReport",
            "POST"
          );
        },
      }),
      getDeviceConsumentStats: builder.mutation({
        query: (data) => {
          return apiFunction(
            data,
            "/api/devices/storageRoles/getDeviceConsumentStats",
            "POST"
          );
        },
      }),
      updateDeviceSyncSetting: builder.mutation({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/updateDeviceSyncSetting", "PATCH");
        },
      }),
      getDeviceSettingLogsPaginated: builder.mutation({
        query: (data) =>
          apiFunction(data, "/api/devices/storageRoles/getDeviceSettingLogsPaginated", "POST"),
      }),
      getDeviceOperationLogsPaginated: builder.mutation({
        query: (data) =>
          apiFunction(data, "/api/devices/storageRoles/getDeviceOperationLogsPaginated", "POST"),
      }),
      getDeviceInfoLogsPaginated: builder.mutation({
        query: (data) =>
          apiFunction(data, "/api/devices/storageRoles/getDeviceInfoLogsPaginated", "POST"),
      }),
      // Makine Kayıtları (Ödemeler) — query: argümana göre önbellek, eski
      // yanıt yenisini ezmez (#242; önce mutation'dı)
      getPaymentOperationLogsPaginated: builder.query({
        query: (data) =>
          apiFunction(data, "/api/devices/storageRoles/getPaymentOperationLogsPaginated", "POST"),
        keepUnusedDataFor: 30,
      }),
    };
  },
});

export const {
  useGetDeviceLastOperationMutation,
  useGetDeviceMovementHistoryQuery,
  useGetDeviceConsumentLogsforReportMutation,
  useGetDeviceDetailPerformanceReportMutation,
  useUpdateDeviceConsumentLogMutation,
  useGetDeviceSettingLogsforReportMutation,
  useGetDeviceManualQuotaLogsforReportMutation,
  useGetDeviceQuotaChangeLogsforReportMutation,
  useGetDeviceInfoLogsforReportMutation,
  useGetDeviceOperationLogsforReportMutation,
  useGetDeviceConsumentStatsMutation,
  useLazyGetDeviceDetailQuery,
  useLazyGetDeviceDistributionQuery,
  useLazyGetDeviceByIdQuery,
  useGetDevicesQuery,
  useUpdateDeviceInformationMutation,
  useUpdateDeviceSerialNoMutation,
  useUpdateDeviceNameMutation,
  useUpdateSettingMutation,
  useDowloadSettingMutation,
  useDeleteDeviceMutation,
  useUploadManualQRMutation,
  useUploadManualQuotaMutation,
  useGetManualQuotaRequestQuery,
  useGetPaymentRejectionsQuery,
  useGenerateManualQuotaRequestCodeMutation,
  useUpdateDeviceSyncMutation,
  useRemoveDeviceProductMutation,
  useUpdateDeviceSyncSettingMutation,
  useGetDeviceSettingLogsPaginatedMutation,
  useGetDeviceOperationLogsPaginatedMutation,
  useGetDeviceInfoLogsPaginatedMutation,
  useGetPaymentOperationLogsPaginatedQuery,
} = devicesApi;
export { devicesApi };
